
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate  } from "react-router-dom";
import "./ParticipantAuctionSession.css";
import {
  Timer,
  Users,
  IndianRupee,
  TrendingDown,
  Send,
  Building,
  MapPin,
  School,
  User,
  Clock,
  AlertTriangle,
  Crown,
  ArrowLeft,
  Info,
  Activity,
  Zap,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import newAuctionService from "../../../services/newAuctionService";
import notificationService from "../../../services/notificationService";
import { API_BASE_URL } from "../../../services/apiConfig";
import InfoModal from "../../Common/InfoModal";
import { rankLabel, isForwardMode, nextBoundary, violatesBoundary, stepFieldLabel, pickBest } from "../../../utils/auctionDirection";

// Interfaces based on the API response
interface Bid {
  id: number;
  user_id: number;
  amount: string;
  created_at: string;
  user?: {
    person_name: string;
    company_name: string;
  } | null;
  time_ago?: string;
}

interface BidRanking {
  user_id: number;
  person_name: string;
  company_name: string;
  bid_count: number;
  last_bid_time: string;
  rank: number;
  amount: string;
}

interface Statistics {
  total_participants: number;
  active_participants: number;
  total_bids: number;
  lowest_bid: number | null;
  highest_bid: number | null;
}

interface Auction {
  id: number;
  title: string;
  description: string;
  auction_date: string;
  start_time: string;
  end_time: string;
  duration?: number;
  currency: string;
  decremental_value: string;
  current_price?: string;
  pre_bid_allowed?: number; // 1 = allowed, 0 = not allowed
  auction_type?: "english_no_tie" | "english" | "traffic_light";
  auction_mode?: "reverse" | "forward";
  status: "upcoming" | "live" | "completed";
  auction_no: string;
  bids: Bid[];
  statistics: Statistics;
  time_value?: string;
  time_status?: string;
  created_by: number;
}

// Modal Component
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "success" | "error";
  title: string;
  message: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, type, title, message }) => {
  if (!isOpen) return null;

  return (
    <div className="pas-modal-overlay" onClick={onClose}>
      <div
        className="pas-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`pas-modal-icon pas-modal-icon-${type}`}>
          {type === "success" ? <CheckCircle /> : <XCircle />}
        </div>
        <h3 className="pas-modal-title">{title}</h3>
        <p className="pas-modal-message">{message}</p>
        <button
          onClick={onClose}
          className={`pas-modal-btn pas-modal-btn-${type}`}
        >
          Close
        </button>
      </div>
    </div>
  );
};

const ParticipantAuctionSession: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [auction, setAuction] = useState<Auction | null>(null);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [bidRanking, setBidRanking] = useState<BidRanking[]>([]);
  const [bidAmount, setBidAmount] = useState<string>("");
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isSubmittingBid, setIsSubmittingBid] = useState(false);
  const [isSubmittingPreBid, setIsSubmittingPreBid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isJoined, setIsJoined] = useState(false);
  const userIsEditingRef = useRef(false);
  const [userIsEditing, setUserIsEditing] = useState(false);
  const prevDecrementRef = useRef<string | null>(null);
  const [showUpdateMsg, setShowUpdateMsg] = useState(false);
  
  // Time extension tracking
  const previousEndTimeRef = useRef<string | null>(null);
  const [showTimeExtensionModal, setShowTimeExtensionModal] = useState(false);
  const [timeExtensionMessage, setTimeExtensionMessage] = useState("");
  
  // Modal state
  const [modal, setModal] = useState<{
    isOpen: boolean;
    type: "success" | "error";
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: "success",
    title: "",
    message: "",
  });

  const showModal = (
    type: "success" | "error",
    title: string,
    message: string
  ) => {
    setModal({ isOpen: true, type, title, message });
  };

  const closeModal = () => {
    setModal((prev) => ({ ...prev, isOpen: false }));
  };


  useEffect(() => {
    if (!auction) return;

    const currentValue = auction.decremental_value;

    if (
      prevDecrementRef.current !== null &&
      prevDecrementRef.current !== currentValue
    ) {
      // 👇 show message when value changes
      setTimeout(() => setShowUpdateMsg(true), 100);

      // Hide after 4 seconds
      const timer = setTimeout(() => setShowUpdateMsg(false), 4000);
      return () => clearTimeout(timer);
    }

    prevDecrementRef.current = currentValue;
  }, [auction?.decremental_value]);

  // Monitor for time extension
  useEffect(() => {
    if (!auction || auction.status !== "live") return;

    const currentEndTime = auction.end_time;
    
    // Check if end time has changed (indicating an extension)
    if (previousEndTimeRef.current && previousEndTimeRef.current !== currentEndTime) {
      // Calculate time remaining
      const endTime = new Date(`${auction.auction_date}T${currentEndTime}`);
      const now = new Date();
      const timeRemaining = endTime.getTime() - now.getTime();
      const minutesRemaining = Math.floor(timeRemaining / (1000 * 60));
      
      // Show extension modal
      setTimeExtensionMessage(
        `⏰ Auction time has been extended! A bid was placed in the last 3 minutes. New end time: ${formatTime(currentEndTime)}. Time remaining: ${minutesRemaining} minutes.`
      );
      setShowTimeExtensionModal(true);
      
      // Auto-close modal after 8 seconds
      setTimeout(() => {
        setShowTimeExtensionModal(false);
      }, 8000);
    }
    
    previousEndTimeRef.current = currentEndTime;
  }, [auction?.end_time, auction?.status, auction?.auction_date]);

  // Helper function to format time
  const formatTime = (timeStr: string) => {
    if (!timeStr) return "N/A";
    try {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };






  const calculateNextBid = (
    currentAuction: Auction,
    rankingData: BidRanking[] = []
  ): string => {
    const decrement = parseFloat(currentAuction.decremental_value);
    const type = currentAuction.auction_type || "english_no_tie";

    // english / traffic_light: next bid is relative to the participant's OWN last bid
    if (type === "english" || type === "traffic_light") {
      const myId = Number(user?.id);
      const mine = rankingData
        .filter((r) => Number(r.user_id) === myId)
        .map((r) => parseFloat(r.amount));
      if (mine.length > 0) {
        return nextBoundary(currentAuction.auction_mode, pickBest(currentAuction.auction_mode, mine) as number, decrement).toString();
      }
      return ""; // first bid for this supplier — no suggested value
    }

    // english_no_tie (default): next bid is relative to the global best
    // (lowest for reverse, highest for forward).
    const bestBid = isForwardMode(currentAuction.auction_mode)
      ? currentAuction.statistics?.highest_bid
      : currentAuction.statistics?.lowest_bid;
    if (bestBid !== null && bestBid !== undefined) {
      return nextBoundary(currentAuction.auction_mode, bestBid, decrement).toString();
    }
    return "";
  };

  const fetchAuctionData = useCallback(async () => {
    if (!id) return;
    try {
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/auction/${id}/participants`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      if (!response.ok) {
        setIsJoined(false);
        throw new Error(
          `Failed to fetch auction details. Status: ${response.status}`
        );
      }
      const data = await response.json();
      if (data.success && data.auction) {
        setAuction(data.auction);
        setStatistics(data.statistics);
        setBidRanking(data.bid_ranking || []);
        const nextBid = calculateNextBid(data.auction, data.bid_ranking || []);
        // Only update bid amount if user is not currently editing the input field
        if (!userIsEditingRef.current) {
          setBidAmount(nextBid);
        }
        setError(null);
      } else {
        throw new Error(data.message || "Invalid auction data received");
      }
    } catch (err: any) {
      setError(err.message);
      setAuction(null);
      if (err.message.includes("Status: 403")) {
        setIsJoined(false);
        showModal("error", "Access Denied", "You may need to join the auction first.");
      } else {
        showModal("error", "Error", err.message);
      }
    }
  }, [id]);


  

  const joinAuction = useCallback(async () => {
    if (!id || !user) return false;
    setLoading(true);
    try {
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/auction/join`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            auction_id: Number(id),
            phone_number: user.phoneNumber,
          }),
        }
      );
      const result = await response.json();
      if (
        response.ok &&
        (result.success || result.message?.includes("already joined"))
      ) {
        showModal("success", "Success", result.message || "Successfully joined auction!");
        setIsJoined(true);
        await fetchAuctionData();
        setLoading(false);
        return true;
      }
      throw new Error(
        result.message || `Failed to join auction. Status: ${response.status}`
      );
    } catch (err: any) {
      setError(err.message);
      showModal("error", "Join Failed", err.message);
      setIsJoined(false);
      setLoading(false);
      return false;
    }
  }, [id, user, fetchAuctionData]);

  useEffect(() => {
    if (id && user) {
      joinAuction();
    }
  }, [id, user, joinAuction]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading && !auction) {
        setLoading(false);
        setError("Loading timeout - please try again");
      }
    }, 30000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (auction && loading) {
      setLoading(false);
    }
  }, [auction, loading]);

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     if (isJoined) {
  //       fetchAuctionData();
  //     }
  //   }, 5000);
  //   return () => clearInterval(interval);
  // }, [isJoined, fetchAuctionData]);

  useEffect(() => {
    userIsEditingRef.current = userIsEditing;
  }, [userIsEditing]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isJoined && !userIsEditingRef.current) {
        fetchAuctionData();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isJoined]);

  // Poll for bid rejection notifications
  useEffect(() => {
    if (!user || !id) return;

    const checkNotifications = async () => {
      try {
        const response = await notificationService.getMyNotifications();
        
        // Extract notifications array from response
        const notifications = Array.isArray(response) 
          ? response 
          : (response as any)?.notifications || [];
        
        // Check for bid rejection notifications for this auction
        const bidRejectionNotifications = notifications.filter(
          (notif: any) => 
            notif.type === 'bid_rejected' && 
            String(notif.auction_id) === String(id) &&
            !notif.is_read
        );

        // Show modal for the first unread rejection notification
        if (bidRejectionNotifications.length > 0) {
          const notif = bidRejectionNotifications[0];
          
          // Show modal with rejection message
          showModal(
            "error",
            "Bid Rejected ⚠️",
            notif.message || "Your bid has been rejected by the auctioneer for this auction."
          );
          
          // Mark notification as read to prevent showing it again
          try {
            await notificationService.markAsRead(notif.id);
          } catch (markError) {
            console.warn('Failed to mark notification as read:', markError);
          }
          
          // Refresh auction data to show updated bid list
          await fetchAuctionData();
        }
      } catch (error) {
        console.warn('Failed to check notifications:', error);
      }
    };

    // Check immediately and then every 5 seconds
    checkNotifications();
    const notificationInterval = setInterval(checkNotifications, 5000);

    return () => clearInterval(notificationInterval);
  }, [user, id, fetchAuctionData]);

  useEffect(() => {
    if (!auction || auction.status !== "live") {
      setTimeLeft(0);
      return;
    }
    const timer = setInterval(() => {
      const endTime = new Date(`${auction.auction_date}T${auction.end_time}`);
      const now = new Date();
      const diff = endTime.getTime() - now.getTime();
      if (diff <= 0) {
        setTimeLeft(0);
        clearInterval(timer);
        if (auction.status !== "completed") {
          setAuction((prev) =>
            prev ? { ...prev, status: "completed" } : null
          );
        }
        return;
      }
      setTimeLeft(diff);
    }, 1000);
    return () => clearInterval(timer);
  }, [auction]);

  const formatTimeRemaining = (milliseconds: number) => {
    if (milliseconds <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    const totalSeconds = Math.floor(milliseconds / 1000);
    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const seconds = totalSeconds % 60;
    return { days, hours, minutes, seconds };
  };

  const handleSubmitBid = async () => {
    if (!auction || !user || !bidAmount) return;
    const bidValue = parseFloat(bidAmount);
    if (isNaN(bidValue) || bidValue <= 0) {
      showModal("error", "Invalid Bid", "Please enter a valid bid amount.");
      return;
    }
    const type = auction.auction_type || "english_no_tie";
    const decrement = parseFloat(auction.decremental_value);
    const forward = isForwardMode(auction.auction_mode);
    if (type === "english" || type === "traffic_light") {
      // Per-supplier rule: bid must beat my own best bid by the step
      // (lower in reverse, higher in forward).
      const myId = Number(user?.id);
      const mine = bidRanking
        .filter((r) => Number(r.user_id) === myId)
        .map((r) => parseFloat(r.amount));
      if (
        mine.length > 0 &&
        violatesBoundary(auction.auction_mode, bidValue, nextBoundary(auction.auction_mode, pickBest(auction.auction_mode, mine) as number, decrement))
      ) {
        showModal(
          "error",
          "Invalid Bid",
          forward 
            ? `Your bid must be at least ${decrement} ${auction.currency} more than your highest bid.`
            : `Your bid must be at least ${decrement} ${auction.currency} less than your last bid.`
        );
        return;
      }
    } else {
      const bestBid = forward ? auction.statistics.highest_bid : auction.statistics.lowest_bid;
      if (bestBid !== null && !forward && bidValue >= bestBid) {
        showModal("error", "Invalid Bid", "Your bid must be lower than the current lowest price.");
        return;
      }
      if (bestBid !== null && forward && bidValue <= bestBid) {
        showModal("error", "Invalid Bid", "Your bid must be higher than the current highest price.");
        return;
      }
    }

    // Check if bid is being placed in the last 3 minutes
    const endTime = new Date(`${auction.auction_date}T${auction.end_time}`);
    const now = new Date();
    const timeRemaining = endTime.getTime() - now.getTime();
    const minutesRemaining = Math.floor(timeRemaining / (1000 * 60));
    const isInLast3Minutes = minutesRemaining <= 3 && minutesRemaining > 0;
    
    setIsSubmittingBid(true);
    try {
      const result = await newAuctionService.placeBid({
        auction_id: Number(auction.id),
        amount: Number(bidValue),
      });
      if (result?.success) {
        // Show success message
        showModal("success", "Bid Submitted", result.message || "Bid submitted successfully!");
        
        // If bid was placed in last 3 minutes, show extension notification
        if (isInLast3Minutes) {
          setTimeout(() => {
            setTimeExtensionMessage(
              `⏰ You placed a bid in the last 3 minutes! The auction time will be extended to allow other participants to respond. Please wait for the updated end time.`
            );
            setShowTimeExtensionModal(true);
            
            // Auto-close after 8 seconds
            setTimeout(() => {
              setShowTimeExtensionModal(false);
            }, 8000);
          }, 2000);
        }
        
        fetchAuctionData();
      } else {
        const msg = result?.message || "Failed to submit bid.";
        showModal("error", "Bid Failed", msg);
        throw new Error(msg);
      }
    } catch (error: any) {
      const msg = error?.message || "Failed to submit bid.";
      if (/forbidden|not allowed|401|403/i.test(msg)) {
        showModal("error", "Access Denied", "Not allowed to bid. Make sure you joined this auction.");
        setIsJoined(false);
      } else {
        showModal("error", "Bid Failed", msg);
      }
    } finally {
      setIsSubmittingBid(false);
    }
  };

  const handlePreBid = async () => {
    if (!auction || !user || !bidAmount) return;
    const bidValue = parseFloat(bidAmount);
    if (isNaN(bidValue) || bidValue <= 0) {
      showModal("error", "Invalid Bid", "Please enter a valid bid amount.");
      return;
    }
    const type = auction.auction_type || "english_no_tie";
    const decrement = parseFloat(auction.decremental_value);
    const forwardPreBid = isForwardMode(auction.auction_mode);
    if (type === "english" || type === "traffic_light") {
      // Per-supplier rule: bid must beat my own best bid by the step
      // (lower in reverse, higher in forward).
      const myId = Number(user?.id);
      const mine = bidRanking
        .filter((r) => Number(r.user_id) === myId)
        .map((r) => parseFloat(r.amount));
      if (
        mine.length > 0 &&
        violatesBoundary(auction.auction_mode, bidValue, nextBoundary(auction.auction_mode, pickBest(auction.auction_mode, mine) as number, decrement))
      ) {
        showModal(
          "error",
          "Invalid Bid",
          forwardPreBid 
            ? `Your bid must be at least ${decrement} ${auction.currency} more than your highest bid.`
            : `Your bid must be at least ${decrement} ${auction.currency} less than your last bid.`
        );
        return;
      }
    } else {
      const bestBid = forwardPreBid ? auction.statistics?.highest_bid : auction.statistics?.lowest_bid;
      if (bestBid !== null && bestBid !== undefined && !forwardPreBid && bidValue >= bestBid) {
        showModal("error", "Invalid Bid", "Your bid must be lower than the current lowest price.");
        return;
      }
      if (bestBid !== null && bestBid !== undefined && forwardPreBid && bidValue <= bestBid) {
        showModal("error", "Invalid Bid", "Your bid must be higher than the current highest price.");
        return;
      }
    }
    setIsSubmittingPreBid(true);
    try {
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/auction/bid`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            auction_id: Number(auction.id),
            amount: Number(bidValue),
          }),
        }
      );
      const result = await response.json();
      if (response.ok && result?.success) {
        showModal("success", "Bid Submitted", result.message || "Bid submitted successfully!");
        fetchAuctionData();
      } else {
        const msg = result?.message || "Failed to submit bid.";
        showModal("error", "Bid Failed", msg);
        throw new Error(msg);
      }
    } catch (error: any) {
      const msg = error?.message || "Failed to submit bid.";
      if (/forbidden|not allowed|401|403/i.test(msg)) {
        showModal("error", "Access Denied", "Not allowed to place bid. Make sure you joined this auction.");
        setIsJoined(false);
      } else {
        showModal("error", "Bid Failed", msg);
      }
    } finally {
      setIsSubmittingPreBid(false);
    }
  };

  if (loading && !auction) {
    return (
      <div className="participant-auction-loading">
        <div className="loading-card">
          <div className="loading-spinner" />
          <p>Joining and loading auction...</p>
        </div>
      </div>
    );
  }

  if (error && !isJoined) {
    return (
      <div className="participant-auction-error">
        <div className="error-card">
          <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
          <h2>Access Denied</h2>
          <p>{error}</p>
          <button onClick={joinAuction} className="error-back-btn">
            <Users className="w-4 h-4" />
            Retry Joining Auction
          </button>
        </div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="participant-auction-error">
        <div className="error-card">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
          <h2>Waiting for Auction Data</h2>
          <p>Successfully joined. Waiting to retrieve auction details...</p>
          <div className="loading-spinner mt-4" />
        </div>
      </div>
    );
  }

  const timeRemaining = formatTimeRemaining(timeLeft);
  const isForwardAuction = isForwardMode(auction.auction_mode);
  const currentLowest = auction.statistics?.lowest_bid;
  const currentHighest = auction.statistics?.highest_bid;
  const currentBest = isForwardAuction ? currentHighest : currentLowest;

  /* ---------- PERMISSION: full ranking only for auctioneer ---------- */
  const myId = Number(user?.id);
  const isAuctioneer = myId === auction.created_by;

  /* ---------- AUCTION TYPE flags ---------- */
  // english_no_tie : original behaviour (L1 value visible, global-best rule)
  // english        : L1 value hidden, ranks-only, per-supplier rule
  // traffic_light  : no values, colour-coded ranks, per-supplier rule
  // Same three types in both directions; forward just mirrors reverse
  // (highest wins, bids must go UP by the increment).
  const auctionType = auction.auction_type || "english_no_tie";
  const isPerSupplier = auctionType === "english" || auctionType === "traffic_light";
  const isTrafficLight = auctionType === "traffic_light";

  // Participants never see bid values for the per-supplier types; auctioneer always does.
  const hideBidValues = !isAuctioneer && isPerSupplier;

  // Traffic Light (participant view): show ONLY the colour signal — no rank labels.
  const trafficLightView = isTrafficLight && !isAuctioneer;

  // The participant's own best bid so far (lowest for reverse, highest for
  // forward) — used for per-supplier validation.
  const myBestBid = (() => {
    const mine = bidRanking
      .filter((r) => Number(r.user_id) === myId)
      .map((r) => parseFloat(r.amount));
    if (mine.length === 0) return null;
    return pickBest(auction.auction_mode, mine);
  })();

  const validateBidAmount = (value: string): string | null => {
    if (!value) return "Please enter a bid amount";

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "Please enter a valid number";
    if (numValue <= 0) return "Bid amount must be greater than 0";

    const step = parseFloat(auction.decremental_value);

    if (isPerSupplier) {
      // Per-supplier: bid must beat the participant's own best bid by the step.
      if (myBestBid !== null && violatesBoundary(auction.auction_mode, numValue, nextBoundary(auction.auction_mode, myBestBid, step))) {
        return isForwardAuction
          ? `Bid must be at least ${step} ${auction.currency} more than your highest bid (${myBestBid})`
          : `Bid must be at least ${step} ${auction.currency} less than your last bid (${myBestBid})`;
      }
      return null;
    }

    if (currentBest !== null && currentBest !== undefined) {
      const boundary = nextBoundary(auction.auction_mode, currentBest, step);
      if (violatesBoundary(auction.auction_mode, numValue, boundary)) {
        return isForwardAuction
          ? `Bid must be at least ${step} ${auction.currency} more than current highest (${currentBest})`
          : `Bid must be at least ${step} ${auction.currency} less than current lowest (${currentBest})`;
      }
    }

    return null;
  };

  /* ---------- PARTICIPANT-VIEW RANKING ---------- */
  // english_no_tie : participant sees L-1 + their own bid only (original behaviour).
  // english / traffic_light : participant sees ALL ranks (values hidden at render).
  // traffic_light          : participant sees ONLY their own bid (the API already
  //                          strips everyone else's; this filter is a second guard).
  const participantSafeRanking =
    isAuctioneer
      ? bidRanking
      : isTrafficLight
      ? (() => {
          const own = bidRanking
            .filter((r) => Number(r.user_id) === myId)
            .sort((a, b) => (isForwardAuction ? -1 : 1) * (parseFloat(a.amount) - parseFloat(b.amount)));
          return own.length > 0 ? [own[0]] : [];
        })()
      : isPerSupplier
      ? bidRanking
      : (() => {
          // 1.  current L-1 (best bid: lowest for reverse, highest for forward)
          const l1 = bidRanking.find((r) => r.rank === 1);
          // 2.  participant’s own best bid
          const own = bidRanking
            .filter((r) => r.user_id === myId)
            .sort((a, b) => (isForwardAuction ? -1 : 1) * (parseFloat(a.amount) - parseFloat(b.amount)))[0];
          // 3.  de-duplicate (if participant is L-1)
          const set = new Map();
          if (l1) set.set(l1.user_id, l1);
          if (own) set.set(own.user_id, own);
          return Array.from(set.values()).sort((a, b) => a.rank - b.rank);
        })();

  const formatClock = (t: string) => {
    const [hh, mm] = t.split(":");
    const h = parseInt(hh, 10);
    return `${h % 12 || 12}:${mm} ${h >= 12 ? "PM" : "AM"}`;
  };
  const formatDate = (d: string) => {
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  };

  return (
    <div className="participant-auction-session">
      <button
        onClick={() => navigate("/dashboard/MyParticipatedA")}
        className="pas-back"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <header className="pas-header">
        <div className="pas-header-info">
          <h1 className="pas-title">{auction.title}</h1>
          <div className="pas-meta">
            <span className="pas-auction-no">
              <Activity /> {auction.auction_no}
            </span>
            <span className="pas-meta-dot" />
            <span>
              {formatDate(auction.auction_date)} ·{" "}
              {formatClock(auction.start_time)} –{" "}
              {formatClock(auction.end_time)}
            </span>
          </div>
        </div>
        <div className={`pas-status ${auction.status}`}>
          <Zap />
          {auction.time_status || auction.status.toUpperCase()}
        </div>
      </header>

      <InfoModal
        isOpen={showUpdateMsg}
        onClose={() => setShowUpdateMsg(false)}
        title="Bid Value Updated"
        message={`The ${stepFieldLabel(auction.auction_mode).toLowerCase()} has been updated to ₹${parseFloat(auction.decremental_value).toLocaleString()}. Please check the current bid amount before placing your bid.`}
      />

      <section className="pas-timer">
        <div className="pas-timer-info">
          <div className="pas-label">
            <Timer />
            {auction.status === "completed"
              ? "Auction Ended"
              : "Auction Closes In"}
          </div>
          {auction.status !== "completed" && (
            <div className="pas-timer-close">
              Closes on {formatDate(auction.auction_date)} at{" "}
              {formatClock(auction.end_time)}
            </div>
          )}
        </div>
        <div className="pas-timer-display">
          <div className="pas-unit">
            <span className="pas-unit-value">{timeRemaining.days}</span>
            <span className="pas-unit-label">Days</span>
          </div>
          <div className="pas-sep">:</div>
          <div className="pas-unit">
            <span className="pas-unit-value">
              {timeRemaining.hours.toString().padStart(2, "0")}
            </span>
            <span className="pas-unit-label">Hours</span>
          </div>
          <div className="pas-sep">:</div>
          <div className="pas-unit">
            <span className="pas-unit-value">
              {timeRemaining.minutes.toString().padStart(2, "0")}
            </span>
            <span className="pas-unit-label">Min</span>
          </div>
          <div className="pas-sep">:</div>
          <div className="pas-unit">
            <span className="pas-unit-value">
              {timeRemaining.seconds.toString().padStart(2, "0")}
            </span>
            <span className="pas-unit-label">Sec</span>
          </div>
        </div>
      </section>

      <div className="pas-layout">
        <div className="pas-main">
          {auction.status === "live" ? (
            <section className="pas-card">
              <div className="pas-card-header">
                <h2 className="pas-card-title">
                  <Send />
                  Submit Your Bid
                </h2>
              </div>
              <div className="pas-card-body">
                <div className="pas-bid-form">
                  <div className="pas-field">
                    <label htmlFor="bidAmount">
                      Your Bid Price ({auction.currency})
                    </label>
                    <input
                      type="text"
                      id="bidAmount"
                      value={bidAmount}
                      onFocus={() => setUserIsEditing(true)} // user entered the field
                      onBlur={() => {
                        setUserIsEditing(false); // user left the field
                        if (bidAmount && !isNaN(parseFloat(bidAmount))) {
                          const err = validateBidAmount(bidAmount);
                          if (err) showModal("error", "Invalid Bid", err);
                          else setBidAmount(parseFloat(bidAmount).toString());
                        }
                      }}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "" || /^\d*\.?\d*$/.test(v)) setBidAmount(v);
                      }}
                      className="pas-input"
                      placeholder="Enter Your Bid Amount"
                    />
                  </div>
                  <button
                    onClick={handlePreBid}
                    disabled={isSubmittingPreBid || isSubmittingBid}
                    className="pas-bid-btn"
                  >
                    {isSubmittingPreBid ? (
                      <div className="loading-spinner" />
                    ) : (
                      <>
                        <Send /> Place Bid
                      </>
                    )}
                  </button>
                </div>
              </div>
            </section>
          ) : (
            <section className="pas-card pas-ended">
              <h3>
                {auction.status === "completed"
                  ? "Auction Completed"
                  : "Auction Not Active"}
              </h3>
              <p>
                This auction has{" "}
                {auction.status === "completed" ? "ended" : "not started yet"}.
              </p>
            </section>
          )}

          {/* ----------- RANKING CARD ----------- */}
          <section className="pas-card">
            <div className="pas-card-header">
              <h2 className="pas-card-title">
                <Crown />
                {isAuctioneer
                  ? "Live Bidding Rankings"
                  : isTrafficLight
                  ? "Your Bid Status"
                  : isPerSupplier
                  ? "Current Rankings"
                  : `Current Best Bid (${rankLabel(auction.auction_mode, 1)})`}
              </h2>
              <p className="pas-card-subtitle">
                {trafficLightView
                  ? `Your bids: ${statistics?.total_bids || 0}`
                  : `Total Bids: ${statistics?.total_bids || 0}`}
              </p>
            </div>
            <div className="pas-card-body">
              <div className="pas-rankings">
                {participantSafeRanking.length > 0 ? (
                  participantSafeRanking.map((bid) => (
                    <div
                      key={bid.user_id}
                      className={`pas-rank-row ${
                        bid.user_id === myId ? "current-user" : ""
                      } ${bid.rank === 1 ? "rank-first" : ""} ${
                        trafficLightView
                          ? bid.rank <= 2
                            ? "tl-yellow"
                            : bid.rank === 3
                            ? "tl-red"
                            : "tl-neutral"
                          : ""
                      }`}
                    >
                      <div className="pas-rank-left">
                        {/* Traffic Light shows only the colour — no rank label */}
                        {!trafficLightView && (
                          <span className="pas-rank-badge">{rankLabel(auction.auction_mode, bid.rank)}</span>
                        )}
                        {bid.user_id === myId && (
                          <span className="pas-you">You</span>
                        )}
                      </div>
                      <div className="pas-rank-price">
                        {trafficLightView ? null : hideBidValues ? (
                          <span className="pas-rank-only">{rankLabel(auction.auction_mode, bid.rank)}</span>
                        ) : (
                          <>
                            {auction.currency}{" "}
                            {parseFloat(bid.amount).toLocaleString()}
                          </>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="pas-empty">
                    <Users />
                    <h4>
                      {trafficLightView
                        ? "You haven't placed a bid yet"
                        : "No Bids Placed Yet"}
                    </h4>
                    <p>
                      {trafficLightView
                        ? "Place your bid above. Only your own bid is shown here."
                        : "Be the first to place a bid in this auction!"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        <aside className="pas-side">
          <section className="pas-card">
            <div className="pas-card-header">
              <h2 className="pas-card-title">
                <Info />
                Auction Details
              </h2>
            </div>
            <dl className="pas-details">
              <div className="pas-detail pas-detail-block">
                <dt>Description</dt>
                <dd>{auction.description || "—"}</dd>
              </div>
              <div className="pas-detail">
                <dt>Date</dt>
                <dd>{formatDate(auction.auction_date)}</dd>
              </div>
              <div className="pas-detail">
                <dt>Start Time</dt>
                <dd>{formatClock(auction.start_time)}</dd>
              </div>
              <div className="pas-detail">
                <dt>End Time</dt>
                <dd>{formatClock(auction.end_time)}</dd>
              </div>
              <div className="pas-detail">
                <dt>{stepFieldLabel(auction.auction_mode)}</dt>
                <dd>
                  {auction.currency}{" "}
                  {parseFloat(auction.decremental_value).toLocaleString()}
                </dd>
              </div>
              <div className="pas-detail">
                <dt>Pre-Bid Allowed</dt>
                <dd>{auction.pre_bid_allowed === 1 ? "Yes" : "No"}</dd>
              </div>
            </dl>
          </section>
        </aside>
      </div>

      {/* Modal Component */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        type={modal.type}
        title={modal.title}
        message={modal.message}
      />

      {/* Time Extension Modal */}
      {showTimeExtensionModal && (
        <div
          className="pas-modal-overlay"
          onClick={() => setShowTimeExtensionModal(false)}
        >
          <div
            className="pas-modal pas-modal-ext"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pas-modal-icon pas-modal-icon-time">
              <Clock />
            </div>
            <h3 className="pas-modal-title">Time Extension!</h3>
            <p className="pas-modal-message">{timeExtensionMessage}</p>
            <button
              onClick={() => setShowTimeExtensionModal(false)}
              className="pas-modal-btn pas-modal-btn-time"
            >
              Got It!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantAuctionSession;


